import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { api } from '../../lib/api'
import { Pager } from '../../lib/Pager'
import { SortTh, sortParam, useSort } from '../../lib/Sort'
import { useAuthedSession } from '../../lib/SessionContext'

/* ============================================================================
   โครงหน้า "ข้อมูลหลัก" ใช้ร่วมทุกตาราง (บริษัท / แผนก / สาขา)

   มีไว้เพราะสามหน้านั้นต่างกันแค่ชื่อคอลัมน์กับ endpoint — ถ้าปล่อยให้ก๊อปกัน
   วันหลังจะแก้ตกหล่นทีละหน้า แล้วสามหน้าที่ควรหน้าตาเหมือนกันจะค่อย ๆ เพี้ยน

   กติกาที่บังคับไว้ในนี้ที่เดียว ทุกหน้าจึงได้เหมือนกันฟรี:
   · ตัวเลข **ชิดขวาและมีคอมมาเสมอ** (col.num → class .num + Intl.NumberFormat)
   · ไม่มีปุ่มลบ มีแต่ปิด/เปิดใช้งาน — รหัสของตารางพวกนี้ถูกอ้างข้ามตารางโดย
     ไม่มี FK ลบทิ้งแล้วข้อมูลเก่าจะหาชื่อไม่เจอ (ดูหัวไฟล์ route ของแต่ละตัว)
   · ปุ่มเขียนขึ้นเฉพาะ superadmin/admin ให้ตรงกับ canWrite ฝั่ง backend
   · ตารางใช้ .table-wrap + table.data เหมือนหน้าอื่นทั้งระบบ
   · ฟอร์มเพิ่ม/แก้เป็น **drawer** (.drawer + .drawer-scrim) ตัวเดียวกับหน้าพนักงาน
     และหน้าอนุมัติคำขอ — ไม่ได้ทำ modal แบบใหม่ เพราะจะกลายเป็น pattern ที่สาม
     ในระบบที่ควรมีแบบเดียว · ปิดได้ 3 ทาง: ปุ่ม ✕ · คลิกฉากหลัง · กด Esc
   · แบ่งหน้าและเรียง **ที่ server** ผ่าน lib/paging.js + lib/sorting.js เหมือนหน้า
     CRUD อื่น (หน้าพวกนี้เป็นหน้าจัดการข้อมูล ไม่ใช่ข้อมูลอ้างอิงสำหรับ dropdown
     ที่ข้อยกเว้น ก. ของ paging.js พูดถึง)
   ============================================================================ */

const nf = new Intl.NumberFormat('th-TH')

export type Col<T> = {
  label: string
  cell: (row: T) => ReactNode
  /** ชิดขวา + ไม่ตัดบรรทัด — ใส่กับคอลัมน์ที่เป็นตัวเลขทุกครั้ง */
  num?: boolean
  /** คีย์เรียงที่ backend รู้จัก (ต้องอยู่ใน allow ของ endpoint) — ไม่ใส่ = เรียงไม่ได้ */
  sortKey?: string
}

export type Field = {
  name: string
  label: string
  maxLength?: number
  required?: boolean
  /** แก้ไม่ได้ตอนแก้แถวเดิม (เช่นรหัสที่เป็นคีย์ให้ตารางอื่นอ้าง) */
  lockOnEdit?: boolean
  /**
   * 'companies' = เป็น <select> รายชื่อบริษัทที่ session มีสิทธิ์ (ใช้ชุดเดียวกับ
   * ช่องกรองด้านบน) — ต้องเปิด companyScoped ด้วย ไม่งั้นรายชื่อจะว่าง
   */
  source?: 'companies'
}

export type MasterTableProps<T> = {
  title: string
  /** base ของ API เช่น /api/admin/departments */
  base: string
  /** คีย์ของแถว ใช้ทั้งเป็น react key และต่อท้าย base เวลาแก้/ปิดใช้งาน */
  keyOf: (row: T) => string | number
  /** ข้อความบอกว่าแถวนี้คืออะไร ใช้ในกล่องยืนยันตอนปิดใช้งาน */
  labelOf: (row: T) => string
  isInactive: (row: T) => boolean
  cols: Col<T>[]
  fields: Field[]
  /** แปลงแถวเป็นค่าเริ่มต้นของฟอร์มตอนกดแก้ */
  toDraft: (row: T) => Record<string, string>
  addLabel: string
  /** หน่วยที่โชว์ในตัวแบ่งหน้า เช่น 'บริษัท' → "ทั้งหมด 2 บริษัท" */
  unit: string
  /**
   * ตารางนี้มี CompanyCode ต่อแถว → โชว์ช่องเลือกบริษัทไว้กรอง
   * (ทะเบียนบริษัทเองไม่ต้องใส่ — มันคือรายการบริษัท ไม่ได้สังกัดบริษัทไหน)
   */
  companyScoped?: boolean
}

type Draft = Record<string, string>

export default function MasterTable<T>({
  title,
  base,
  keyOf,
  labelOf,
  isInactive,
  cols,
  fields,
  toDraft,
  addLabel,
  unit,
  companyScoped,
}: MasterTableProps<T>) {
  const session = useAuthedSession()
  const mayWrite =
    session.kind === 'admin' && (session.role === 'superadmin' || session.role === 'admin')

  const [rows, setRows] = useState<T[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  // เปลี่ยนการเรียง = กลับไปหน้า 1 ไม่งั้นจะค้างอยู่หน้าที่ไม่มีแถวแล้ว
  const sort = useSort([], () => setPage(1))
  /** '' = ทุกบริษัทที่มีสิทธิ์ · อื่น ๆ = เจาะบริษัทเดียว */
  const [companyCode, setCompanyCode] = useState('')
  const [companies, setCompanies] = useState<{ code: string; name: string | null }[]>([])
  /** null = ฟอร์มปิด · '' = กำลังเพิ่มใหม่ · อื่น ๆ = คีย์ของแถวที่กำลังแก้ */
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>({})

  const blank = useCallback(
    () => Object.fromEntries(fields.map((f) => [f.name, ''])) as Draft,
    [fields],
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
      const sp = sortParam(sort.sort)
      if (sp) qs.set('sort', sp)
      if (companyCode) qs.set('companyCode', companyCode)

      const d = await api<{ rows: T[]; total: number }>(`${base}?${qs}`)
      setRows(d.rows)
      setTotal(d.total)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [base, page, pageSize, sort.sort, companyCode])

  /* รายชื่อบริษัทที่เลือกได้ — โหลดครั้งเดียว มาจากขอบเขตของ session (hrCompanyCodes)
     ไม่ใช่ทั้งตาราง Companies ถ้าโหลดไม่ได้ก็ปล่อยให้ช่องว่าง ดีกว่าทำทั้งหน้าพัง */
  useEffect(() => {
    if (!companyScoped) return
    let alive = true
    api<{ options: { code: string; name: string | null }[] }>('/api/admin/companies/options')
      .then((d) => alive && setCompanies(d.options))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [companyScoped])

  useEffect(() => {
    void load()
  }, [load])

  /** แก้อะไรไปแล้วบ้าง — ใช้เตือนก่อนปิดทิ้ง */
  const dirty = editing !== null && Object.values(draft).some((v) => v.trim() !== '')

  const close = useCallback(() => {
    if (dirty && !window.confirm('ยังไม่ได้บันทึก — ปิดแล้วสิ่งที่แก้จะหายไป ปิดเลยไหม')) return
    setEditing(null)
    setError(null)
  }, [dirty])

  // Esc ปิดแผง — ผูกที่ document เพราะโฟกัสอาจอยู่ที่ช่องไหนก็ได้ในแผง
  useEffect(() => {
    if (editing === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [editing, close])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (editing === null) return
    setBusy(true)
    try {
      await api(editing === '' ? base : `${base}/${encodeURIComponent(editing)}`, {
        method: editing === '' ? 'POST' : 'PUT',
        body: JSON.stringify(draft),
      })
      setEditing(null)
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function toggleActive(row: T) {
    const turningOff = !isInactive(row)
    if (turningOff && !confirm(`ปิดใช้งาน ${labelOf(row)} ?`)) return
    setBusy(true)
    try {
      await api(`${base}/${encodeURIComponent(String(keyOf(row)))}/inactive`, {
        method: 'POST',
        body: JSON.stringify({ inactive: turningOff }),
      })
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h1>{title}</h1>

      {error && editing === null ? <p className="error">{error}</p> : null}
      {!rows && !error ? <p>กำลังโหลด…</p> : null}

      {companyScoped && companies.length > 1 ? (
        <p>
          <label>
            บริษัท
            <select
              value={companyCode}
              onChange={(e) => {
                setCompanyCode(e.target.value)
                setPage(1)
              }}
            >
              <option value="">ทุกบริษัท</option>
              {companies.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name ? `${c.code} — ${c.name}` : c.code}
                </option>
              ))}
            </select>
          </label>
        </p>
      ) : null}

      {mayWrite && editing === null ? (
        <p>
          <button
            type="button"
            onClick={() => {
              setEditing('')
              setDraft(blank())
              setError(null)
            }}
          >
            {addLabel}
          </button>
        </p>
      ) : null}

      {editing !== null ? (
        <>
          {/* ฉากหลังต้อง unmount จริงตอนปิด ห้ามซ่อนด้วย display:none ค้างไว้ —
              index.css ใช้ body:has(.drawer-scrim) ล็อกการเลื่อนหน้าข้างหลัง */}
          <button type="button" className="drawer-scrim" onClick={close} aria-label="ปิดแผง" />

          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label={editing === '' ? addLabel : `แก้ ${editing}`}
          >
            <header className="drawer-head">
              <div>
                <p className="drawer-title">{editing === '' ? addLabel : 'แก้ข้อมูล'}</p>
                <p className="drawer-sub">{editing === '' ? title : `${title} · ${editing}`}</p>
              </div>
              <button type="button" className="drawer-close" onClick={close} aria-label="ปิด">
                ✕
              </button>
            </header>

            <form onSubmit={(e) => void save(e)}>
              <div className="drawer-body">
                {error ? <p className="error">{error}</p> : null}

                {fields.map((f) => {
                  const locked = editing !== '' && f.lockOnEdit
                  const lockedWhy = locked
                    ? 'ค่านี้เป็นคีย์ที่ตารางอื่นอ้างอยู่ แก้ไม่ได้'
                    : undefined

                  return (
                    <label key={f.name}>
                      {f.label}
                      {f.source === 'companies' ? (
                        <select
                          value={draft[f.name] ?? ''}
                          required={f.required}
                          disabled={locked}
                          title={lockedWhy}
                          onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value })}
                        >
                          <option value="">— เลือกบริษัท —</option>
                          {companies.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.name ? `${c.code} — ${c.name}` : c.code}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={draft[f.name] ?? ''}
                          maxLength={f.maxLength}
                          required={f.required}
                          disabled={locked}
                          title={lockedWhy}
                          onChange={(e) => setDraft({ ...draft, [f.name]: e.target.value })}
                        />
                      )}
                    </label>
                  )
                })}
              </div>

              <div className="drawer-foot">
                <button type="submit" disabled={busy}>
                  {busy ? 'กำลังบันทึก…' : 'บันทึก'}
                </button>{' '}
                <button type="button" onClick={close} disabled={busy}>
                  ยกเลิก
                </button>
              </div>
            </form>
          </aside>
        </>
      ) : null}

      {rows ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                {cols.map((c) =>
                  c.sortKey ? (
                    <SortTh
                      key={c.label}
                      label={c.label}
                      sortKey={c.sortKey}
                      sort={sort.sort}
                      onToggle={sort.toggle}
                      className={c.num ? 'num' : undefined}
                    />
                  ) : (
                    <th key={c.label} className={c.num ? 'num' : undefined}>
                      {c.label}
                    </th>
                  ),
                )}
                <SortTh label="สถานะ" sortKey="status" sort={sort.sort} onToggle={sort.toggle} />
                {mayWrite ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const off = isInactive(row)
                return (
                  <tr key={String(keyOf(row))} className={off ? 'inactive' : undefined}>
                    {cols.map((c) => {
                      const v = c.cell(row)
                      return (
                        <td key={c.label} className={c.num ? 'num' : undefined}>
                          {c.num && typeof v === 'number' ? nf.format(v) : v}
                        </td>
                      )
                    })}
                    <td>{off ? 'ปิดใช้งาน' : 'ใช้งานอยู่'}</td>
                    {mayWrite ? (
                      <td>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setEditing(String(keyOf(row)))
                            setDraft(toDraft(row))
                            setError(null)
                          }}
                        >
                          แก้
                        </button>{' '}
                        <button type="button" onClick={() => void toggleActive(row)} disabled={busy}>
                          {off ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                )
              })}
            </tbody>
          </table>
          {rows.length === 0 ? <p>ไม่มีข้อมูล</p> : null}
        </div>
      ) : null}

      {rows ? (
        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          unit={unit}
          onPage={setPage}
          onPageSize={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      ) : null}
    </>
  )
}
